import { memo, useCallback, useContext, useEffect, useState } from 'react'
import { useMemo } from 'react'

import {
  Box,
  CircularProgress,
  Link,
  Typography,
  useTheme,
} from '@mui/material'
import { IconLink } from '@tabler/icons-react'
import type { CRMObject, Page, ThreadMessage } from 'types/graphql'

import Row from 'src/components/Row/Row'
import TiptapThreadMessage from 'src/components/Tiptap/TiptapThreadMessage/TiptapThreadMessage'
import WorkspaceMemberChip from 'src/components/WorkspaceMemberChip/WorkspaceMemberChip'
import { DayContext } from 'src/lib/dayContext'
import { logger } from 'src/lib/logger'
import { NativeObjectTypes } from 'src/lib/objects'

import ThreadPageButton from '../ThreadPageButton/ThreadPageButton'
import { htmlStyles } from '../threads'
import ThreadTiptap from '../ThreadTiptap/ThreadTiptap'

import { ContextStatus } from './Provider/ThreadContext'
import useThread from './Provider/useThread'
import ThreadArtifactButton from './ThreadArtifact'
import ThreadContextPlan from './ThreadContextPlan'
import ThreadContextResult from './ThreadContextResult'
import ThreadToolCall from './ThreadToolCall'
import ThreadUpdate from './ThreadUpdate'

interface MessageProps {
  message: ThreadMessage
  workspaceId: string
}

const MessageComponent = ({ message, workspaceId }: MessageProps) => {
  const theme = useTheme()
  const { setSidebarObject } = useContext(DayContext)
  const content = message.content

  const [initialized, setInitialized] = useState(false)

  const styles = useMemo(() => {
    return htmlStyles(theme)
  }, [theme])

  const isUserMessage = message.userId && message.userId !== 'day_ai_assistant'

  useEffect(() => {
    if (!initialized) {
      setTimeout(() => {
        setInitialized(true)
      }, 1000)
    }
  }, [initialized])

  logger.dev('initialized', initialized)

  const handleOpenPage = useCallback(
    ({ page }: { page: Page }) => {
      setSidebarObject({
        objectType: NativeObjectTypes.Page,
        objectId: page.id,
        properties: page,
      })
    },
    [setSidebarObject]
  )
  const getMessageContent = useCallback(() => {
    if (typeof content === 'string') {
      return (
        <Typography
          className="thread-message-text"
          sx={{ ...styles, px: '12px' }}
        >
          {content}
        </Typography>
      )
    }

    return (
      <Box className="thread-message-content">
        {message.type === 'text' && (
          <Typography
            className="thread-message-text"
            sx={{ ...styles, p: '12px' }}
          >
            {content.text}
          </Typography>
        )}
        {message.type === 'html' && <TiptapThreadMessage message={message} />}

        {message.type === 'tool' && (
          <ThreadToolCall toolCall={message.content as any} />
        )}

        {message.type === 'update' && (
          <ThreadUpdate update={message.content as any} />
        )}

        {message.type === 'artifact' && (
          <ThreadArtifactButton artifact={message.content as any} />
        )}

        {message.type === 'context-plan' && (
          <ThreadContextPlan
            plan={message.content as any}
            threadMessageId={message.id}
          />
        )}

        {message.type === 'context-result' && (
          <ThreadContextResult
            result={message.content as { objects: CRMObject[] }}
            threadMessageId={message.id}
          />
        )}

        {message.links?.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {message.links.map((link, i) => (
              <Link
                key={i}
                href={link.url}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                  cursor: 'pointer',
                  color: (theme) => theme.palette.secondary.main,
                }}
              >
                <IconLink size={16} />
                {link.text}
              </Link>
            ))}
          </Box>
        )}

        {message.pages?.length > 0 && (
          <Box
            sx={{
              px: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {message.pages.map((page, i) => (
              <ThreadPageButton
                key={i}
                page={page}
                onClick={handleOpenPage}
              />
            ))}
          </Box>
        )}
      </Box>
    )
  }, [message, handleOpenPage, styles, content])

  return isUserMessage ? (
    <Row
      key={message.id || 'user-message'}
      className={`thread-message thread-message-user ${
        initialized ? 'initialized' : ''
      }`}
      id={`message-${message.id}`}
    >
      <Box className="thread-message-avatar">
        <WorkspaceMemberChip
          userId={message.userId}
          variant="avatar"
          workspaceId={workspaceId}
        />
      </Box>
      <Typography>{content.text}</Typography>
    </Row>
  ) : (
    <Box
      key={message.id || 'ai-message'}
      id={`message-${message.id}`}
      className={`thread-message ${initialized ? 'initialized' : ''}`}
    >
      {getMessageContent()}
    </Box>
  )
}

const Message = memo(MessageComponent, (prev, next) => {
  return prev.message?.id === next.message?.id
})

const ThreadMessages = () => {
  const { state, handleAiResponse } = useThread()

  const workspaceId = state?.thread?.workspaceId

  const messages = useMemo(() => {
    return [...(state.thread?.messages || [])].sort((a, b) => {
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    })
  }, [state.thread?.messages])

  logger.dev('loadingMessage', state.loadingMessage)

  return messages.length > 0 ? (
    <Box className="thread-messages-container">
      <Box
        className="thread-messages"
        id="thread-messages"
      >
        {messages.map((message) => (
          <Message
            key={message.id}
            message={message}
            workspaceId={workspaceId}
          />
        ))}
        {state.pendingAiPrompt && state.token && (
          <Box className="thread-message thread-message-ai">
            {state.contextStatus === ContextStatus.SUCCESS ? (
              <Box className="thread-message-content">
                <ThreadTiptap
                  token={state.token}
                  contextString={state.contextString}
                  handleAiResponse={handleAiResponse}
                  currentQuery={state.pendingAiPrompt}
                />
              </Box>
            ) : (
              state.loadingMessage && (
                <Box className="thread-messages-loading">
                  <CircularProgress size={16} />
                  <Typography>{state.loadingMessage.label}</Typography>
                </Box>
              )
            )}
          </Box>
        )}
        {state.loadingMessage && (
          <Row className="thread-messages-loading">
            <CircularProgress size={16} />
            <Typography>{state.loadingMessage.label}</Typography>
          </Row>
        )}
        <Box
          className={`thread-messages-end-divider ai-drafting ${
            state.pendingAiPrompt ? 'ai-drafting' : ''
          }`}
        ></Box>
      </Box>
    </Box>
  ) : state.loadingMessage ? (
    <Row className="thread-messages-loading">
      <CircularProgress size={16} />
      <Typography>{state.loadingMessage.label}</Typography>
    </Row>
  ) : !state.thread ? (
    <Box className="thread-create-loading-container">
      <Row className="thread-create-loading">
        <CircularProgress size={16} />
        <Typography>Loading thread ...</Typography>
      </Row>
    </Box>
  ) : (
    <></>
  )
}

export default ThreadMessages
